<script setup lang="ts">
// ================================
// « D'où vient ce montant » : le tableau des postes de la facture
// d'acheminement, servi par le moteur (DEV-2168), et le seuil qui a décidé
// l'état (DEV-2183). Rien n'est recalculé ici.
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatNumber } from "@core/helpers"
import {
    cmdpsApplicable,
    getCmdpsEstimeeEur,
    getEconomieFermeEur,
    getUnitePuissance,
    lireDecompositionTurpe,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE,
    LIBELLE_TOTAL_HORS_DEPASSEMENTS,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    libellePlancherRetenu,
    lirePlancherRetenu,
    phraseSeuilDEtat,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"
import {
    classeMontant,
    formatMontantSigne,
    nombreEurosDepassement,
} from "@modules/courtage/utils/optim-turpe-v2.montant"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // W81 (14/10/2026) : « puissance minimale », pas « plancher ».
            floorNote: "Puissance minimale retenue : {plancher}",
            turpe: {
                // Le montant porte la PÉRIODE analysée, pas une année (A4).
                // « poste » désigne une période tarifaire (Q6) ; ici, une
                // composante de la facture.
                poste: "Composante de la facture (€ HT)",
                avant: "Avant",
                apres: "Après",
                // Revue du 08/10 (R1) : l'effet de chaque poste pour le
                // client, à la convention de toute la page : « + » il
                // économise, « − » il paie plus. Les postes somment au titre.
                variation: "Économie",
            },
        },
    },
})

type Props = { etude: OptimTurpeV2Etude }
type TurpeRow = {
    label: string
    nature: "poste" | "total" | "hors-total" | "nette"
    avant: string
    apres: string
    variation: string
    /** Le montant de la colonne « Économie », pour sa couleur. */
    variationEur: number | null
}

const props = defineProps<Props>()

/** Montant € HT groupé fr-FR avec unité monétaire collée (ex. « 4 200 € »). */
function eur(value?: number | null): string {
    if (typeof value !== "number") return "—"
    return formatNumber(value, {
        unit: "€",
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    })
}

/**
 * L'économie servie sur un poste, signée à la convention de la page (revue du
 * 08/10, R1) : une baisse de la facture de 563 € se lit « +563 € », une hausse
 * « −84 € », 0 reste « 0 € ». L'économie nette suit la même écriture que le
 * titre, et les postes somment à elle.
 */
function variation(economie: number | null): string {
    return formatMontantSigne(economie) ?? ""
}

/**
 * Le plancher que le MOTEUR a retenu, quand il le sert (courtage#254,
 * DEV-2185) ; rien en C2 sans saisie. La page n'annonce aucun plancher par
 * segment (A6).
 */
const getFloorNote = computed(() => {
    const plancher = lirePlancherRetenu(props.etude)
    if (!plancher) return ""
    return t("floorNote", {
        plancher: libellePlancherRetenu(
            plancher,
            getUnitePuissance(props.etude)
        ),
    })
})

/**
 * La décomposition TURPE réconciliée (DEV-2168) : chaque poste servi par le
 * moteur, le total ferme, la CMDPS hors total et l'économie nette.
 */
const getTurpe = computed(() => lireDecompositionTurpe(props.etude))

/** La période de l'étude est-elle d'un an (365 ou 366 jours, Q15) ? */
const getParAn = computed(() => {
    const jours = lireChiffre(props.etude).periode.jours
    return jours === 365 || jours === 366
})
const getTurpeRows = computed<TurpeRow[]>(() => {
    const lignes: TurpeRow[] = getTurpe.value.lignes.map((ligne) => ({
        // Q15 (maquette finale du 15/10/2026) : « Économie nette par an »
        // sur une période d'un an, « de la période » sinon.
        label:
            ligne.nature === "nette" && getParAn.value
                ? "Économie nette par an"
                : ligne.libelle,
        nature: ligne.nature,
        avant: ligne.avantEur === null ? "" : eur(ligne.avantEur),
        apres: ligne.apresEur === null ? "" : eur(ligne.apresEur),
        variation: variation(ligne.economieEur),
        variationEur: ligne.economieEur,
    }))
    if (getTurpe.value.reconciliee) return lignes
    // Sans réconciliation servie (étude antérieure) : les deux parts, en deux
    // lignes du tableau (maquette allégée : les anciens encadrés « Part
    // liée… » et « Dépassements… »). Une part non servie se lit « — »,
    // jamais « 0 € » (revue C4).
    const ferme = getEconomieFermeEur(props.etude)
    lignes.push({
        label: LIBELLE_TOTAL_HORS_DEPASSEMENTS,
        nature: "total",
        avant: "",
        apres: "",
        variation: ferme === null ? "—" : variation(ferme),
        variationEur: ferme,
    })
    if (cmdpsApplicable(props.etude)) {
        const cmdps = getCmdpsEstimeeEur(props.etude)
        lignes.push({
            label: `${LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE}, hors de ce total`,
            nature: "hors-total",
            avant: "",
            // Le coût estimé des dépassements du scénario retenu : un montant
            // « après », pas une variation.
            // Décision du pilote (14/10/2026) : sous 0,50 €, « moins de 1 € »,
            // jamais « 0 € ».
            apres:
                cmdps === null
                    ? "—"
                    : Math.abs(cmdps) > 0 && Math.abs(cmdps) < 0.5
                      ? `${nombreEurosDepassement(cmdps)}\u00a0€`
                      : eur(cmdps),
            variation: "",
            variationEur: null,
        })
    }
    return lignes
})

/**
 * Le seuil qui a décidé une économie, en français client (DEV-2183). Sous les
 * autres états, il explique l'état et se lit à côté du chiffre (temps 1).
 */
const getInegalite = computed(() =>
    lireChiffre(props.etude).etat === "economie"
        ? phraseSeuilDEtat(props.etude)
        : null
)
</script>

<template>
    <!-- ================================ -->
    <!-- « D'où vient ce montant » (maquette allégée du 07/10/2026) : le
         tableau des postes, avant → après, la part des dépassements hors du
         total, l'économie nette, puis le seuil qui a décidé l'état. Le tableau
         des puissances avant → après n'est plus ici : c'est celui de « À
         demander » (temps 4) ; les deux encadrés « Part liée… » et
         « Dépassements… » sont devenus les lignes du tableau. -->
    <!-- ================================ -->
    <div class="optim-turpe-v2-decomposition flex min-w-0 flex-col gap-2">
        <div class="overflow-x-auto">
            <table class="w-full min-w-[34rem] text-sm">
                <thead class="text-xs text-primary-500">
                    <tr>
                        <th class="py-1 pr-3 text-left font-normal">
                            {{ t("turpe.poste") }}
                        </th>
                        <th class="py-1 pr-3 text-right font-normal">
                            {{ t("turpe.avant") }}
                        </th>
                        <th class="py-1 pr-3 text-right font-normal">
                            {{ t("turpe.apres") }}
                        </th>
                        <th class="py-1 text-right font-normal">
                            {{ t("turpe.variation") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in getTurpeRows"
                        :key="row.label"
                        class="border-t"
                        :class="{
                            // Revue du support du 08/10 (A3) : un filet
                            // sépare le total et la nette des postes.
                            'border-surface-100':
                                row.nature === 'poste' ||
                                row.nature === 'hors-total',
                            'border-surface-400':
                                row.nature === 'total' ||
                                row.nature === 'nette',
                            'font-semibold':
                                row.nature === 'total' ||
                                row.nature === 'nette',
                            'text-[var(--p-orange-700)]':
                                row.nature === 'hors-total',
                        }"
                        :data-testid="`turpe-ligne-${row.nature}`"
                    >
                        <!-- Revue 2 en production (I13, I60) : le libellé
                             prend ce qui reste, les montants ne passent
                             jamais à la ligne. -->
                        <td class="w-auto py-1 pr-3 text-primary-900">
                            {{ row.label }}
                        </td>
                        <td
                            class="py-1 pr-3 text-right whitespace-nowrap text-primary-700 tabular-nums"
                            data-testid="turpe-montant"
                        >
                            {{ row.avant }}
                        </td>
                        <td
                            class="py-1 pr-3 text-right whitespace-nowrap text-primary-900 tabular-nums"
                            data-testid="turpe-montant"
                        >
                            {{ row.apres }}
                        </td>
                        <td
                            class="py-1 text-right whitespace-nowrap tabular-nums"
                            :class="
                                classeMontant(
                                    row.variationEur,
                                    'text-primary-900'
                                )
                            "
                            data-testid="turpe-economie"
                        >
                            {{ row.variation }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p v-if="getFloorNote" class="text-xs text-primary-500">
            {{ getFloorNote }}
        </p>
        <p
            v-if="getInegalite"
            class="text-xs text-primary-500"
            data-testid="optim-turpe-v2-inegalite"
        >
            {{ getInegalite }}
        </p>
    </div>
</template>
